/**
 * Green Mitt Kit — the steps section.
 *
 * Owns: check-offs (persisted per device), the sticky progress bar, and the
 * dwell timer. Broadcasts a `gmk:progress` CustomEvent on `document` whenever
 * progress changes (and mirrors the latest payload on `window.gmkProgressState`
 * so sections that initialise later can catch up). Listens for `gmk:resume`
 * from the hero's "Pick up at step N" button.
 */
(function () {
  var STATE_VERSION = 1;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function readState(key, total) {
    var raw;
    try {
      raw = JSON.parse(window.localStorage.getItem(key) || 'null');
    } catch (e) {
      return [];
    }
    if (!raw || raw.v !== STATE_VERSION || raw.total !== total || !Array.isArray(raw.done)) {
      // Steps were added or rewritten since this visitor last saved — start clean
      // rather than checking off the wrong instructions.
      return [];
    }
    return raw.done;
  }

  function writeState(key, total, done) {
    try {
      window.localStorage.setItem(key, JSON.stringify({ v: STATE_VERSION, total: total, done: done }));
    } catch (e) {
      /* Private browsing or a full quota — progress just won't persist. */
    }
  }

  function formatClock(seconds) {
    var m = Math.floor(seconds / 60);
    var s = seconds % 60;
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  window.GmkSteps = {
    init: function (rootId) {
      var root = document.getElementById(rootId);
      if (!root || root.dataset.gmkStepsInitialized) return;
      root.dataset.gmkStepsInitialized = 'true';

      var storageKey = root.dataset.gmkStorageKey || 'gmk-steps-v1';
      var steps = Array.prototype.slice.call(root.querySelectorAll('[data-gmk-step]'));
      if (!steps.length) return;

      var total = steps.length;
      var progressWrap = root.querySelector('[data-gmk-progress]');
      var progressFill = root.querySelector('[data-gmk-progress-fill]');
      var progressLabel = root.querySelector('[data-gmk-progress-label]');

      function doneIds() {
        return steps
          .filter(function (step) {
            return step.classList.contains('is-done');
          })
          .map(function (step) {
            return step.dataset.gmkStep;
          });
      }

      function firstUnchecked() {
        for (var i = 0; i < steps.length; i += 1) {
          if (!steps[i].classList.contains('is-done')) return steps[i];
        }
        return null;
      }

      function broadcast(done) {
        var next = firstUnchecked();
        var state = {
          sectionId: rootId,
          done: done,
          total: total,
          nextStep: next ? next.dataset.gmkStep : null
        };
        window.gmkProgressState = state;
        document.dispatchEvent(new CustomEvent('gmk:progress', { detail: state }));
      }

      function refresh(persist) {
        var done = doneIds();
        if (persist) writeState(storageKey, total, done);

        if (progressWrap) progressWrap.hidden = done.length === 0;
        if (progressFill) progressFill.style.width = Math.round((done.length / total) * 100) + '%';
        if (progressLabel) {
          progressLabel.textContent =
            done.length === total ? 'All done — nice work' : done.length + ' of ' + total + ' steps done';
        }

        broadcast(done.length);
      }

      function setDone(step, isDone) {
        var button = step.querySelector('[data-gmk-check]');
        step.classList.toggle('is-done', isDone);
        if (button) button.setAttribute('aria-pressed', isDone ? 'true' : 'false');
      }

      /* ---------- Restore saved progress ---------- */
      var saved = readState(storageKey, total);
      steps.forEach(function (step) {
        if (saved.indexOf(step.dataset.gmkStep) !== -1) setDone(step, true);

        var button = step.querySelector('[data-gmk-check]');
        if (!button) return;
        button.addEventListener('click', function () {
          setDone(step, !step.classList.contains('is-done'));
          refresh(true);
        });
      });

      /* ---------- Dwell timers ---------- */
      root.querySelectorAll('[data-gmk-timer]').forEach(function (timer) {
        var step = timer.closest('[data-gmk-step]');
        var duration = parseInt(timer.dataset.gmkTimerSeconds, 10) || 180;
        var display = timer.querySelector('[data-gmk-timer-display]');
        var msg = timer.querySelector('[data-gmk-timer-msg]');
        var startBtn = timer.querySelector('[data-gmk-timer-start]');
        var resetBtn = timer.querySelector('[data-gmk-timer-reset]');
        if (!display || !startBtn || !resetBtn) return;

        var remaining = duration;
        var interval = null;
        var idleLabel = startBtn.textContent.trim();
        var runningLabel = (msg && msg.dataset.gmkTimerRunningLabel) || idleLabel;
        var doneMessage = (msg && msg.dataset.gmkTimerDoneMsg) || '';
        var baseTitle = document.title;

        function render() {
          var clock = formatClock(remaining);
          display.textContent = clock;
          if (interval) document.title = clock + ' — ' + baseTitle;
        }

        function stop() {
          if (interval) {
            window.clearInterval(interval);
            interval = null;
          }
          document.title = baseTitle;
          startBtn.textContent = idleLabel;
        }

        startBtn.addEventListener('click', function () {
          if (interval) return;
          if (msg) msg.textContent = '';
          startBtn.textContent = runningLabel;
          interval = window.setInterval(function () {
            remaining -= 1;
            if (remaining <= 0) {
              remaining = 0;
              render();
              stop();
              if (msg) msg.textContent = doneMessage;
              if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
              // Finishing the dwell time IS finishing the step, so check it off.
              if (step && !step.classList.contains('is-done')) {
                setDone(step, true);
                refresh(true);
              }
              return;
            }
            render();
          }, 1000);
        });

        resetBtn.addEventListener('click', function () {
          stop();
          remaining = duration;
          if (msg) msg.textContent = '';
          render();
        });

        window.addEventListener('pagehide', stop);
        render();
      });

      /* ---------- Resume from the hero ---------- */
      document.addEventListener('gmk:resume', function () {
        var target = firstUnchecked();
        if (!target) return;
        target.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'start'
        });
        var button = target.querySelector('[data-gmk-check]');
        if (button) button.focus({ preventScroll: true });
      });

      refresh(false);
    }
  };
})();
